import 'regenerator-runtime/runtime';

import * as React from 'react';
import * as names from './constants';

import { run } from '@co4cloud/base';
import { LoadingIndicator } from '@co4cloud/components';
import { getRootTrail } from '@co4cloud/graph';
import { type InitialTile, type TileComponentProps } from 'piral-dashboard';
import { OidcErrorType } from 'piral-oidc';
import { Redirect } from 'react-router-dom';
import { loadConfig, type AppConfig } from '../config';
import { client, clearOidcSession } from './oauth';
import { logoutAndClearSessionUi } from './logout';

// One-shot guard so a failed OIDC callback triggers a single clean-login recovery rather than
// an immediate logout that can loop back into the same failure (CO4-3273). Kept in sessionStorage
// (not localStorage) so it is per-tab and never leaks across sessions.
const AUTH_RECOVERY_FLAG = 'co4:auth-recovery-attempted';
// import './sentry';

// disable the piral state view by default in console
sessionStorage.setItem('dbg:view-state', 'off');

if (process.env.DEBUG_PILET === 'on') {
  // default behavior is "off", but we want to load pilets from the included feed
  sessionStorage.setItem('dbg:load-pilets', 'on');
}

const Dashboard = React.lazy(() => import('./pages/Dashboard'));
const NoObjectsView = React.lazy(() => import('./pages/Dashboard').then((mod) => ({ default: mod.NoObjectsView })));
const Layout = React.lazy(() => import('./components/Layout'));
const Greetings = React.lazy(() => import('./tiles/GreetingsTile'));
const Weather = React.lazy(() => import('./tiles/WeatherTile'));

// Wrapper for /objects and /objects/:trail. Redirects to the default app when the
// user has root objects; otherwise renders the empty-state view (CO4-3489).
const ObjectsRootRoute = (props: any) => {
  const trail = getRootTrail();
  if (!trail) {
    return <NoObjectsView />;
  }
  return <Redirect to={names.objectsArgsSubPath(names.DEFAULT_APP, trail, '', '')} {...props} />;
};

function makeTile(
  component: React.ComponentType<TileComponentProps>,
  initialColumns: number,
  initialRows: number,
): InitialTile {
  return {
    component,
    preferences: {
      initialColumns,
      initialRows,
    },
  };
}

async function init() {
  try {
    const config: AppConfig = await loadConfig();
    const { shouldRender, state } = await client.handleAuthentication();

    // Callback processed cleanly — reset the recovery guard so a future genuine failure can recover.
    sessionStorage.removeItem(AUTH_RECOVERY_FLAG);

    // Get the final redirect URI from the OIDC state parameter
    // This is set via signInRedirectParams when initiating login
    let finalRedirectUri = state?.finalRedirectUri;

    // If finalRedirectUri is /auth or /logout, redirect to root instead
    // This can happen if login was triggered from those pages
    if (finalRedirectUri) {
      const uriPath = new URL(finalRedirectUri).pathname;
      if (uriPath.startsWith('/auth') || uriPath.startsWith('/logout')) {
        finalRedirectUri = location.origin + '/';
      }
    }
    if (finalRedirectUri) {
      // Only redirect if we're not already on that page
      if (location.href !== finalRedirectUri) {
        window.location.replace(finalRedirectUri);
        return;
      }
    }

    if (shouldRender) {
      await run({
        client,
        feedUrl: config.FEED_URL,
        apiUrl: config.API_URL,
        appEnv: config.APP_ENV as 'development' | 'production',
        Layout: ({ children }) => (
          <React.Suspense fallback={<LoadingIndicator />}>
            <Layout>
              {/* Inner boundary so lazy route chunks suspend HERE, swapping only the content
                  area — without unmounting the Layout (and its Sidebar). Otherwise the first,
                  uncached navigation to a lazy route remounts the Sidebar, re-running its
                  collapse-on-mobile effect and closing the drawer below 991px (CO4-3509). */}
              <React.Suspense fallback={<LoadingIndicator />}>{children}</React.Suspense>
            </Layout>
          </React.Suspense>
        ),
        // Order matters: the greeting is global-0, the weather widget is global-1.
        // DashboardContainer renders global-1 directly below the greeting (CO4-3334).
        // Weather forwards props so its config (location, etc.) from the dashboard config reaches it.
        tiles: [makeTile(() => <Greetings />, 16, 4), makeTile((props) => <Weather {...(props as any)} />, 16, 4)],
        sidebars: {},
        routes: {
          // temporary transitioning routes for DM2. Remove once app use is stable.
          [names.objectsDetailsAppPath]: (props) => (
            <Redirect to={names.transitioningRoute(props.match.params)} {...props} />
          ),
          // permanent routes - order matters
          [names.objectsRootPath]: ObjectsRootRoute,
          [names.objectsTrailPath()]: ObjectsRootRoute,
          // [names.objectsArgsPath()]: (props) => (
          //   <Dashboard {...props} app={props.match.params.app} trail={props.match.params.trail} />
          // ),
          [names.objectsArgsSubPath()]: (props) => (
            <Dashboard {...props} app={props.match.params.app} trail={props.match.params.trail} />
          ),
        },
      });
    }
  } catch (error) {
    console.error(error);

    // A failed OIDC signin callback (OidcErrorType.oidcCallback) is most often stale cross-user
    // state lingering in the shared localStorage store after a user switch (CO4-3273). Rather than
    // force-logging-out — which stranded users in a login/logout loop — clear that state and retry
    // a clean login ONCE. The sessionStorage guard ensures a genuinely broken callback still falls
    // back to logout instead of looping.
    const isCallbackError = (error as { type?: OidcErrorType } | undefined)?.type === OidcErrorType.oidcCallback;
    const alreadyRecovered = sessionStorage.getItem(AUTH_RECOVERY_FLAG) === '1';

    if (isCallbackError && !alreadyRecovered) {
      sessionStorage.setItem(AUTH_RECOVERY_FLAG, '1');
      await clearOidcSession();
      await client.login();
      return;
    }

    await logoutAndClearSessionUi();
  }
}

init();
